type Token = symbol | any;

export const enum InstanceLifetime {
  // Specifies that a single instance of the service will be created.
  Singleton,
  // Specifies that a new instance of the service will be created every time it is requested.
  Transient,
}

interface RegisterOptions {
  lifetime?: InstanceLifetime;
  multi?: boolean;
}
const enum ProviderType {
  Factory,
  Value,
}

export interface FactoryFunc<T> {
  (container: Container, ...params: any[]): T;
}

interface FactoryProvider<T = any> {
  instance?: T;
  isResolved: boolean;
  factory: FactoryFunc<T>;
  type: ProviderType.Factory;
  options: RegisterOptions;
}

interface ValueProvider<T = any> {
  value: T;
  type: ProviderType.Value;
  options: RegisterOptions;
}

type Provider<T = any> = FactoryProvider<T> | ValueProvider<T>;

export class Container {
  private providers: Map<Token, Provider[] | Provider> = new Map();

  /**
   * Register a factory function for a token
   * @param token - Symbol or class to use as a token
   * @param factory - Function that creates the instance
   * @param options - Register options
   */
  public register<T>(
    token: Token,
    factory: FactoryFunc<T>,
    options: RegisterOptions = {
      lifetime: InstanceLifetime.Transient,
      multi: false,
    }
  ): void {
    const provider: Provider<T> = {
      factory,
      options,
      type: ProviderType.Factory,
      isResolved: false,
    };

    this.registerProvider(token, provider);
  }
  /**
   * Register a value directly
   * @param token - Symbol or class to use as a token
   * @param value - The value to register
   * @param options - Register options
   */
  public registerValue<T>(
    token: Token,
    value: T,
    options: RegisterOptions = {
      lifetime: InstanceLifetime.Transient,
      multi: false,
    }
  ): void {
    const provider: Provider<T> = { value, options, type: ProviderType.Value };

    this.registerProvider(token, provider);
  }

  public resolve<T>(token: Token): T;
  public resolve<T>(token: Token, options: { optional: true }): T | null;
  public resolve<T>(token: Token, options: { optional: false }): T;
  public resolve<T>(token: Token, options: { optional: boolean } = { optional: false }): T {
    const providers = this.providers.get(token);

    if (!providers && !options.optional) {
      console.error(`[Container error]: No providers found for token ${token}`);
      throw new Error(`No providers found for token ${token}`);
    }

    if (!providers) {
      return null as T;
    }

    if (Array.isArray(providers)) {
      return providers.map((provider) => this.resolveProvider(provider)) as T;
    }

    return this.resolveProvider(providers) as T;
  }

  public has(token: Token): boolean {
    return this.providers.has(token);
  }

  public resolveWithParams<T>(token: Token, ...params: any[]): T {
    const providers = this.providers.get(token);

    if (!providers) {
      throw new Error(`No provider found for token ${token}`);
    }

    if (Array.isArray(providers)) {
      return providers.map((provider) => this.resolveProvider(provider, params)) as T;
    }

    if (providers.type === ProviderType.Value) {
      throw new Error('Cannot resolve a value provider with params');
    }

    return providers.factory(this, params);
  }

  public unregister(token: Token): void {
    this.providers.delete(token);
  }

  private resolveProvider<T>(provider: Provider<T>, params?: any): T {
    if (provider.type === ProviderType.Value) {
      return provider.value;
    }

    if (provider.options.lifetime === InstanceLifetime.Singleton) {
      if (!provider.isResolved) {
        provider.instance = provider.factory(this, params);
        provider.isResolved = true;
      }

      return provider.instance as T;
    }

    return provider.factory(this, params);
  }

  private registerProvider(token: Token, provider: Provider): void {
    if (!provider.options.multi) {
      this.providers.set(token, provider);
      return;
    }

    const existing = this.providers.get(token);
    if (existing !== undefined && !Array.isArray(existing)) {
      throw new Error('Cannot register a multi-provider for a non-array token');
    }

    this.providers.set(token, [...(existing || []), provider]);
  }
}
